import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TrendingUp, MapPin, Calendar, IndianRupee, Target, ArrowRight, Filter } from 'lucide-react';

const OngoingProjectsPage = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    sector: 'all',
    state: 'all',
    budgetRange: 'all'
  });

  useEffect(() => {
    fetchOngoingProjects();
  }, []);

  const fetchOngoingProjects = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      
      // Fetch all active engagements (no auth required for public view)
      const response = await fetch(`${backendUrl}/api/saas/engagements/public`);
      
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      } else {
        console.error('Failed to fetch ongoing projects');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ongoing projects:', error);
      setLoading(false);
    }
  };

  const getProgressPercentage = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    const total = end - start;
    const elapsed = now - start;
    
    if (elapsed < 0) return 0;
    if (elapsed > total) return 100;
    
    return Math.round((elapsed / total) * 100);
  };

  const getStatusColor = (progress) => {
    if (progress >= 75) return '#4CAF50';
    if (progress >= 50) return '#2A8378';
    if (progress >= 25) return '#E4B646';
    return '#1E3A5F';
  };

  const getBudgetLabel = (budget) => {
    if (budget < 25) return '₹10-25L';
    if (budget < 50) return '₹25-50L';
    if (budget < 100) return '₹50L-1Cr';
    return '₹1Cr+';
  };

  const filteredProjects = projects.filter(project => {
    if (filters.sector !== 'all' && project.sector !== filters.sector) return false;
    if (filters.state !== 'all' && project.state !== filters.state) return false;
    if (filters.budgetRange !== 'all') {
      const budget = project.totalBudget;
      if (filters.budgetRange === 'small' && budget >= 25) return false;
      if (filters.budgetRange === 'medium' && (budget < 25 || budget >= 100)) return false;
      if (filters.budgetRange === 'large' && budget < 100) return false;
    }
    return true;
  });

  const sectors = ['Education', 'Healthcare', 'Environment', 'Skilling', 'Women Empowerment', 'Rural Development'];
  const states = ['Rajasthan', 'Maharashtra', 'Madhya Pradesh', 'Gujarat', 'Uttar Pradesh', 'Karnataka'];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{borderColor: '#14B8A6'}}></div>
          <p className="mt-4 text-gray-600">Loading ongoing projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 md:px-8" style={{background: 'linear-gradient(135deg, #1E3A5F 0%, #2A8378 100%)'}}>
        <div className="max-w-6xl mx-auto">
          <h1 className="text-5xl font-bold mb-4 text-white">Ongoing CSR Projects</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Real-time view of active CSR initiatives across India — facilitated by TMG Impact
          </p>
        </div>
      </section>

      {/* Stats Banner */}
      <section className="py-8 px-4 md:px-8 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>{projects.length}</div>
              <div className="text-sm text-gray-600">Active Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1" style={{color: '#2A8378'}}>
                {projects.reduce((sum, p) => sum + p.totalBudget, 0).toFixed(0)}L
              </div>
              <div className="text-sm text-gray-600">Total Budget (₹)</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1" style={{color: '#E4B646'}}>
                {new Set(projects.map(p => p.sector)).size}
              </div>
              <div className="text-sm text-gray-600">Sectors Covered</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold mb-1" style={{color: '#1E3A5F'}}>
                {new Set(projects.map(p => p.state)).size}
              </div>
              <div className="text-sm text-gray-600">States Reached</div>
            </div>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-6 px-4 md:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <Filter className="w-5 h-5" style={{color: '#2A8378'}} />
            <h3 className="font-bold" style={{color: '#1E3A5F'}}>Filter Projects</h3>
          </div>
          <div className="flex flex-wrap gap-4">
            <select
              value={filters.sector}
              onChange={(e) => setFilters({...filters, sector: e.target.value})}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2"
              style={{focusRing: '#2A8378'}}
            >
              <option value="all">All Sectors</option>
              {sectors.map(sector => (
                <option key={sector} value={sector}>{sector}</option>
              ))}
            </select>

            <select
              value={filters.state}
              onChange={(e) => setFilters({...filters, state: e.target.value})}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2"
            >
              <option value="all">All States</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>

            <select
              value={filters.budgetRange}
              onChange={(e) => setFilters({...filters, budgetRange: e.target.value})}
              className="px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2"
            >
              <option value="all">All Budgets</option>
              <option value="small">₹10-25 Lakh</option>
              <option value="medium">₹25L - 1 Crore</option>
              <option value="large">₹1 Crore+</option>
            </select>

            {(filters.sector !== 'all' || filters.state !== 'all' || filters.budgetRange !== 'all') && (
              <button
                onClick={() => setFilters({sector: 'all', state: 'all', budgetRange: 'all'})}
                className="px-4 py-2 text-sm font-semibold rounded-lg transition-all hover:bg-gray-200"
                style={{color: '#2A8378'}}
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          {filteredProjects.length === 0 ? (
            <div className="text-center py-20">
              <Target className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-xl text-gray-600 mb-4">No projects match your filters</p>
              <button
                onClick={() => setFilters({sector: 'all', state: 'all', budgetRange: 'all'})}
                className="px-6 py-3 rounded-lg font-semibold transition-all hover:shadow-lg"
                style={{background: '#2A8378', color: '#FFFFFF'}}
              >
                View All Projects
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {filteredProjects.map((project) => {
                const progress = getProgressPercentage(project.startDate, project.endDate);
                const statusColor = getStatusColor(progress);
                
                return (
                  <div key={project.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-xl transition-all">
                    {/* Project Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2" style={{color: '#1E3A5F'}}>
                          {project.projectTitle}
                        </h3>
                        <div className="flex flex-wrap gap-2 mb-3">
                          <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{background: 'rgba(42, 131, 120, 0.1)', color: '#2A8378'}}>
                            {project.sector}
                          </span>
                          <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{background: 'rgba(228, 182, 70, 0.1)', color: '#B8860B'}}>
                            {project.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Project Details */}
                    <div className="space-y-3 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="w-4 h-4 flex-shrink-0" style={{color: '#2A8378'}} />
                        <span>{project.state}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <IndianRupee className="w-4 h-4 flex-shrink-0" style={{color: '#E4B646'}} />
                        <span>Budget: {getBudgetLabel(project.totalBudget)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Calendar className="w-4 h-4 flex-shrink-0" style={{color: '#1E3A5F'}} />
                        <span>{new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}</span>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-semibold text-gray-600">Project Progress</span>
                        <span className="text-sm font-bold" style={{color: statusColor}}>{progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className="h-3 rounded-full transition-all"
                          style={{background: statusColor, width: `${progress}%`}}
                        ></div>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {project.description}
                    </p>

                    {/* Footer */}
                    <div className="pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-500 mb-3">
                        <TrendingUp className="inline-block w-3 h-3 mr-1" />
                        Facilitated by TMG Impact Xchange
                      </div>
                      <button
                        onClick={() => navigate('/xchange')}
                        className="w-full px-4 py-2 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all hover:shadow-lg border-2"
                        style={{borderColor: '#2A8378', color: '#2A8378'}}
                      >
                        Explore Similar Opportunities
                        <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8" style={{background: '#1E3A5F'}}>
        <div className="max-w-4xl mx-auto text-center">
          <h3 className="text-3xl font-bold mb-6 text-white">Want to Join This Impact Movement?</h3>
          <p className="text-xl text-white/90 mb-8">
            List your CSR intent or project on TMG Xchange and find the perfect partner
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/xchange/corporate/register')}
              className="px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl"
              style={{background: '#2A8378', color: '#FFFFFF'}}
            >
              I'm a Corporate Sponsor
            </button>
            <button
              onClick={() => navigate('/xchange/ngo/register')}
              className="px-8 py-4 rounded-lg font-semibold border-2 transition-all hover:bg-white hover:text-gray-900"
              style={{borderColor: '#FFFFFF', color: '#FFFFFF'}}
            >
              I'm an NGO / Agency
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default OngoingProjectsPage;
